import {
  ResponsiveContainer, ComposedChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceDot,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import type { ChangeEvent } from '@/lib/api/performance'
import type { Metric } from '@/lib/products/salesSeriesTransforms'
import { PerformanceChartTooltip } from './PerformanceChartTooltip'

export interface ChartRow { date: string; value: number; maValue: number | null; cmpValue: number | null }

export interface PerformanceChartProps {
  data: ChartRow[]
  events: ChangeEvent[]
  metric: Metric
  onEventClick: (event: ChangeEvent) => void
}

const DAY_MS = 86_400_000

/**
 * Ordnet jedes Event seiner umschließenden Achsen-Kategorie zu (Intervall
 * [Kat_i, Kat_{i+1})). Funktioniert für täglich (Schritt 1 Tag) wie 7-täglich
 * (Schritt 7 Tage). Das letzte Bucket reicht bis Kat_last + Schrittweite (exkl.);
 * Events außerhalb [erste Kat, Ende letztes Bucket) werden ignoriert.
 */
function groupEventsByCategory(events: ChangeEvent[], categories: string[]): Map<string, ChangeEvent[]> {
  const map = new Map<string, ChangeEvent[]>()
  if (categories.length === 0) return map
  const cats = [...categories].sort()
  const ms = cats.map((c) => Date.parse(`${c}T00:00:00Z`))
  const step = ms.length >= 2 ? ms[ms.length - 1] - ms[ms.length - 2] : DAY_MS
  const upper = ms[ms.length - 1] + step
  for (const ev of events) {
    const day = Date.parse(`${ev.timestamp.slice(0, 10)}T00:00:00Z`)
    if (Number.isNaN(day) || day < ms[0] || day >= upper) continue
    let idx = 0
    for (let i = 0; i < ms.length; i++) {
      if (ms[i] <= day) idx = i
      else break
    }
    const cat = cats[idx]
    const arr = map.get(cat) ?? []
    arr.push(ev)
    map.set(cat, arr)
  }
  return map
}

export function PerformanceChart({ data, events, metric, onEventClick }: PerformanceChartProps) {
  const { t } = useTranslation()
  const eventsByDay = groupEventsByCategory(events, data.map((d) => d.date))
  const fmt = (v: number) => (metric === 'revenue' ? `${v.toFixed(2)} €` : String(Math.round(v)))

  return (
    <div className="w-full h-64">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-neutral-200 dark:stroke-neutral-700" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={56} tickFormatter={fmt} />
          <Tooltip content={(props) => (
            <PerformanceChartTooltip
              active={props.active}
              payload={props.payload as { name?: string; value?: number | null; dataKey?: string; color?: string }[]}
              label={props.label as string}
              eventsByDay={eventsByDay}
              metric={metric}
            />
          )} />

          <Bar dataKey="value" name={t(`performance.tooltip.${metric}`)}
            fill="var(--color-primary-500, #FF6B00)" radius={[3, 3, 0, 0]} maxBarSize={32} />
          <Line dataKey="maValue" name={t('performance.chart.ma')} dot={false}
            strokeWidth={2} stroke="var(--color-primary-400, #FF8C00)" connectNulls />
          <Line dataKey="cmpValue" name={t('performance.chart.comparison')} dot={false}
            strokeWidth={1.5} strokeDasharray="4 4" stroke="var(--color-neutral-400, #a3a3a3)" connectNulls />

          {[...eventsByDay.entries()].map(([day, evs]) => (
            <ReferenceDot key={day} x={day} y={0} r={6}
              fill="var(--color-amber-500, #f59e0b)" stroke="#fff" strokeWidth={1.5}
              ifOverflow="extendDomain" onClick={() => onEventClick(evs[0])} style={{ cursor: 'pointer' }} />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
